import { ZxcvbnFactory } from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import * as zxcvbnFrPackage from '@zxcvbn-ts/language-fr';

/**
 * Politique de mot de passe (NIST SP 800-63B / OWASP ASVS) : la longueur
 * prime sur la complexité imposée — pas de règle majuscule/chiffre/symbole
 * obligatoire (ça pousse vers des motifs prévisibles, `P@ssw0rd1`), à la
 * place on borne la longueur et on rejette les mots de passe devinables via
 * zxcvbn (motifs clavier, dictionnaire, dates, répétitions — pas juste des
 * classes de caractères).
 *
 * MAX_LENGTH=72 : bcrypt tronque silencieusement au-delà de 72 OCTETS —
 * deux mots de passe différents au-delà de cette limite peuvent produire le
 * même hash. On borne en caractères (pas en octets) par simplicité ; avec
 * des caractères multi-octets (accents, emoji) la marge réelle est plus
 * courte que 72 octets, mais 72 caractères reste très généreux pour une
 * phrase de passe et évite d'avoir à exposer une logique d'octets côté UI.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 72;

/** Score zxcvbn minimal accepté (échelle 0-4) — seuil recommandé OWASP ASVS. */
const MIN_SCORE = 3;

// Instance unique — construite une fois (charger les dictionnaires a un coût
// non négligeable), réutilisée par tous les appels d'`evaluatePassword`.
const zxcvbnInstance = new ZxcvbnFactory({
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
    ...zxcvbnFrPackage.dictionary,
  },
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  translations: zxcvbnFrPackage.translations,
});

export interface PasswordEvaluation {
  ok: boolean;
  /** Score zxcvbn 0-4, disponible même si `ok` est faux (pour la jauge UI). */
  score: number;
  /** Raison(s) de rejet, en français, prêtes à afficher — vide si `ok`. */
  errors: string[];
}

/**
 * Supprime accents/casse/espaces et ponctuation — trouvé en test manuel
 * (2026-09-23) : "mot de passe" (12 caractères, espaces compris) passait la
 * politique avec un score de 3-4/4. zxcvbn reconnaît bien "motdepasse" collé
 * comme un mot du dictionnaire faible, mais avec des espaces il traite
 * chaque mot comme un token séparé et note la séquence "mot"+"de"+"passe"
 * comme une phrase de passe légitime (bonus de sécurité), pas comme une
 * évasion du dictionnaire. On score donc AUSSI la version normalisée pour
 * détecter ce contournement, sans pénaliser une vraie phrase de passe
 * ("correct horse battery staple" reste à 4/4 normalisée, car les mots
 * concaténés ne forment toujours pas une entrée de dictionnaire connue).
 */
function normalizeForScoring(password: string): string {
  return password
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Valide un mot de passe contre la politique du produit. `userInputs`
 * (email, nom du tenant, nom affiché...) est passé à zxcvbn pour pénaliser
 * un mot de passe qui les contiendrait — jamais une garantie de sécurité en
 * soi, mais un cas trivial fréquent (mot de passe == email).
 */
export function evaluatePassword(password: string, userInputs: string[] = []): PasswordEvaluation {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return { ok: false, score: 0, errors: [`${PASSWORD_MIN_LENGTH} caractères minimum.`] };
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return { ok: false, score: 0, errors: [`${PASSWORD_MAX_LENGTH} caractères maximum.`] };
  }

  const rawResult = zxcvbnInstance.check(password, userInputs);
  const normalized = normalizeForScoring(password);
  // Rien à gagner à re-scorer si la normalisation ne change rien (déjà tout
  // en minuscules sans espaces/accents) — évite un calcul zxcvbn en double.
  const normResult = normalized !== password ? zxcvbnInstance.check(normalized, userInputs) : rawResult;

  const worst = normResult.score < rawResult.score ? normResult : rawResult;
  if (worst.score < MIN_SCORE) {
    const suggestion = worst.feedback.warning || worst.feedback.suggestions[0] || 'Mot de passe trop facile à deviner.';
    return { ok: false, score: worst.score, errors: [suggestion] };
  }

  return { ok: true, score: worst.score, errors: [] };
}

const HIBP_RANGE_URL = 'https://api.pwnedpasswords.com/range/';

async function sha1Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-1', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

export interface PwnedCheckResult {
  /** true si le mot de passe apparaît dans le corpus Have I Been Pwned. */
  pwned: boolean;
  /** Nombre d'occurrences dans des fuites connues (0 si non trouvé, ou si le check n'a pas pu être fait). */
  count: number;
}

/**
 * Vérifie un mot de passe contre le corpus Have I Been Pwned (900M+ mots de
 * passe réellement compromis, toutes langues confondues) — complète zxcvbn,
 * qui ne connaît que des dictionnaires figés par langue et rate donc toute
 * variante hors de ces listes (trouvé en test manuel : "phrase de passe"
 * passait zxcvbn sans problème, alors que "motdepasse" y était détecté).
 *
 * Modèle k-anonymity : seuls les 5 premiers caractères du hash SHA-1 sont
 * envoyés à l'API — jamais le mot de passe, jamais le hash complet. L'API
 * renvoie tous les suffixes connus partageant ce préfixe (des centaines en
 * moyenne) ; la comparaison avec le suffixe réel se fait uniquement en
 * local. Aucune donnée identifiable ne quitte jamais l'appelant.
 *
 * Fail-open sur toute erreur réseau/API — un tiers indisponible ne doit
 * jamais bloquer un changement de mot de passe légitime (même logique que
 * le rate-limit Redis de AuthService, voir CLAUDE.md).
 */
export async function checkPwnedPassword(password: string): Promise<PwnedCheckResult> {
  try {
    const hash = await sha1Hex(password);
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);

    const res = await fetch(`${HIBP_RANGE_URL}${prefix}`, {
      headers: { 'Add-Padding': 'true' },
    });
    if (!res.ok) return { pwned: false, count: 0 };

    const body = await res.text();
    for (const line of body.split('\n')) {
      const [lineSuffix, countStr] = line.trim().split(':');
      if (lineSuffix === suffix) {
        return { pwned: true, count: Number.parseInt(countStr ?? '0', 10) || 0 };
      }
    }
    return { pwned: false, count: 0 };
  } catch {
    return { pwned: false, count: 0 };
  }
}
